import { Fragment, useEffect, useState } from "react";
import { api, ApiError } from "./api";
import { TulostePohja } from "./Tuloste";
import { KOHDAT } from "./LinjaTarkastus";
import { jarjestaHitsit } from "./hitsijarjestys";
import { naytaHitsinumero } from "./HitsiTunnus";
import { naytaPvm, naytaPvmKello } from "./pvm";
import { kuvanNimi } from "./kuvanimi";
import { t, tDok, virheTeksti } from "./kieli";

/* T635 (Toni 20.10.2026 klo 07.06 ja 07.13): linjatarkastusraportti hyväksytystä tarkastuksesta, Industrialin omasta datasta. A4, yksi
   kutsu (GET /api/linjat/:id/linjatarkastusraportti). Pohja on ytimen TulostePohja; tämä tiedosto tietää vain linjatarkastuksesta.
   Sisältö: otsikkotiedot, tarkistuslista 7.1–7.9 (tulos, havainto, 7.2:n VT-peruste T642), punchit kuvineen, isometrin ja valokuvien
   merkinnät nastoineen, tarkastuskuvat, asiakkaan edustaja ja allekirjoitus tai paperipöytäkirja, hitsiluettelo VT- ja NDT-tiloineen
   (T641:n järjestyksessä). Tuodulle tarkastukselle lähde. Tyhjä osio sanoo olevansa tyhjä — ei katoa. */

type Raportti = {
  linja: { id: number; tunnus: string; kohde_tunnus: string; kohde_nimi: string; lehdet: string } | null;
  tarkastus: {
    id: number; pvm: string | null; tarkastaja: string | null; tila: string; huomiot: string | null; isantajarjestelma: string | null;
    hyvaksyja: string | null; kohdat: Record<string, { tulos: string; huomio?: string; vt_peruste?: string }>;
    allekirjoitus_liite_id: number | null; allekirjoitus_nimi: string | null; allekirjoitettu: string | null;
    hyvaksynta: { asiakkaan_edustaja: string | null; hetki: string | null; a_poikkeama_peruste: string | null; avoimia_a_hetkella: number | null } | null;
  } | null;
  tarkastusliitteet: { id: number; alkuperainen: string; mime: string; laji: string | null; lisatty: string; nimi: string | null; kuvalaji: string | null }[];
  punchit: { id: number; vika: string; luokka: string | null; vastuu: string | null; linjatarkastus_kohta: string | null; hitsinumero: string | null;
    lehti: string | null; kuittaus_pvm: string | null; avoin: boolean; nasta: number | null; kuvat: number[] }[];
  pohjat: { laji: "isometri" | "kuva"; id: number; lehti_nro: number | null; lehti_kpl: number | null; revisio: string | null; piirustusnumero: string | null; kierto: number;
    nimi: string | null; kuvalaji: string | null; lisatty: string | null }[];
  nastat: { numero: number; pohja: string; isometri_id: number | null; kuva_liite_id: number | null; x: number; y: number; vika: string; luokka: string | null; avoin: boolean }[];
  hitsit: { id: number; hitsinumero: string; piirustuslehti: string | null; hitsityyppi: string | null; korjaa_hitsi_id: number | null; vaihe: string | null;
    paiva: string | null; ei_toteudu: boolean | null; hitsaaja: string | null; vt: string; ndt: { menetelma: string; tulos: string | null; raportti: string | null }[] }[];
  tulostettu: string;
};

const SIVUNVAIHTO = { breakBefore: "page" as const };

const tulosSana = (v: string | undefined) => !v ? tDok("ltar.ei_tarkastettu") : v === "ok" ? "OK" : v === "punch" ? "Punch" : v === "na" ? "N/A" : v;
const vtSana = (v: string) => v === "hyvaksytty" ? tDok("ltr.vt_hyvaksytty") : v === "hylatty" ? tDok("ltr.vt_hylatty")
  : v === "ilman_tulosta" ? tDok("ltr.vt_ilman_tulosta") : tDok("ltr.vt_puuttuu");

/** Merkintäpohja nastoineen: valokuva kierrettynä (253) — nastat alkuperäisen kuvan koordinaateissa kuten merkintänäkymässä. */
function Pohja({ p, nastat }: { p: Raportti["pohjat"][number]; nastat: Raportti["nastat"] }) {
  const omat = nastat.filter((n) => p.laji === "isometri" ? n.isometri_id === p.id : n.kuva_liite_id === p.id);
  const src = p.laji === "isometri" ? api.isometrinSivu(p.id, 2400) : api.liiteOsoite(p.id);
  const [koko, setKoko] = useState<{ w: number; h: number } | null>(null);
  const pysty = p.kierto === 90 || p.kierto === 270;
  const nakyva = koko ? (pysty ? { w: koko.h, h: koko.w } : koko) : null;
  // Kuvataso alkuperäisen kuvan kokoisena (prosentteina käännetystä laatikosta) ja käännettynä keskipisteensä ympäri.
  const taso = koko && nakyva ? { position: "absolute" as const, left: "50%", top: "50%", width: `${koko.w / nakyva.w * 100}%`, height: `${koko.h / nakyva.h * 100}%`,
    transform: `translate(-50%, -50%) rotate(${p.kierto}deg)` } : { position: "absolute" as const, inset: 0 };
  return (
    <figure className="tuloste__lohko" style={{ margin: "8px 0" }} data-ltr-pohja={`${p.laji}:${p.id}`}>
      <div className="tuloste__pohja" style={nakyva ? { aspectRatio: `${nakyva.w} / ${nakyva.h}` } : undefined}>
        <div style={taso}>
          <img src={src} alt="" style={{ width: "100%", height: "100%" }}
            onLoad={(e) => setKoko({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })} />
          {omat.map((n) => (
            <span key={n.numero} className={`tuloste__nasta${n.avoin ? "" : " tuloste__nasta--korjattu"}`}
              style={{ left: `${n.x * 100}%`, top: `${n.y * 100}%`, transform: `translate(-50%, -50%) rotate(${-p.kierto}deg)` }}>{n.numero}</span>
          ))}
        </div>
      </div>
      <figcaption className="dim">
        {p.laji === "isometri"
          ? tDok("ltr.isometri_kuvateksti", { lehti: `${p.lehti_nro ?? "?"}${p.lehti_kpl ? `/${p.lehti_kpl}` : ""}`, rev: p.revisio ?? "–", n: omat.length })
          : tDok("ltr.valokuva_kuvateksti", { nimi: kuvanNimi(p, tDok), n: omat.length })}
      </figcaption>
    </figure>
  );
}

/* Keskeneräisenä (Toni 22.10.2026 klo 11.45): kesken olevan tarkastuksen tuloste. Merkintä «KESKENERÄINEN – EI HYVÄKSYTTY» ja
   tulostusaika jokaisella sivulla (pohjan merkintä), kaikki yhdeksän kohtaa ja täyttämätön «ei arvioitu», ei allekirjoitusta eikä
   allekirjoituskenttää (edustajan osio pois). Hyväksytyn tarkastuksen raportti ei muutu. */
export default function LinjatarkastusRaportti({ linjaId, sulje, keskenerainen = false }: { linjaId: number; sulje: () => void; keskenerainen?: boolean }) {
  const [r, setR] = useState<Raportti | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  useEffect(() => {
    api.linjatarkastusraportti(linjaId).then((x) => setR(x as Raportti))
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltr.lataus_virhe")));
  }, [linjaId]);
  if (virhe) return <div className="tuloste-overlay"><p className="huom huom--vika" role="alert">{virhe}</p>
    <button className="nappi nappi--kevyt" onClick={sulje}>{t("tuloste.sulje")}</button></div>;
  if (!r) return <div className="tuloste-overlay"><p className="dim">{t("ltar.ladataan")}</p></div>;
  const ta = r.tarkastus;
  const kuvat = r.tarkastusliitteet.filter((l) => !l.laji && l.mime.startsWith("image/"));
  const paperit = r.tarkastusliitteet.filter((l) => l.laji === "allekirjoitettu_poytakirja");
  const hitsit = jarjestaHitsit(r.hitsit);
  const vtPuuttuu = r.hitsit.filter((h) => h.vaihe === "hitsattu" && !h.ei_toteudu && h.vt !== "hyvaksytty").length;
  const hyvaksytty = ta?.tila === "hyvaksytty";
  return (
    <TulostePohja tunniste="linjatarkastusraportti" sulje={sulje} otsikko={tDok("ltr.otsikko")}
      merkinta={keskenerainen ? tDok("ltr.keskenerainen_merkinta", { aika: naytaPvmKello(r.tulostettu) ?? "" }) : undefined}
      meta={<>
        <div className="mono"><b data-ltr-linja={r.linja?.tunnus ?? ""}>{r.linja?.tunnus ?? ""}</b></div>
        <div>{r.linja?.kohde_tunnus} · {r.linja?.kohde_nimi}</div>
        {r.linja?.lehdet && <div>{tDok("ltr.lehdet", { l: r.linja.lehdet })}</div>}
      </>}>
      <section className="tuloste__lohko" data-ltr-osio="otsikko">
        <table className="tuloste__osapuolet"><tbody>
          <tr><th>{tDok("ltr.paiva")}</th><td>{ta?.pvm ? naytaPvm(ta.pvm) : "–"}</td></tr>
          <tr><th>{tDok("ltr.tarkastaja")}</th><td>{ta?.tarkastaja ?? "–"}</td></tr>
          <tr><th>{tDok("ltr.tila")}</th><td data-ltr-tila={ta?.tila ?? ""}>{hyvaksytty ? tDok("ltr.hyvaksytty") : tDok("ltr.ei_hyvaksytty")}
            {hyvaksytty && ta?.hyvaksyja ? ` · ${ta.hyvaksyja}` : ""}{ta?.hyvaksynta?.hetki ? ` · ${naytaPvm(ta.hyvaksynta.hetki.slice(0, 10))}` : ""}</td></tr>
          {ta?.isantajarjestelma === "sharepoint" && (
            <tr><th>{tDok("ltr.lahde")}</th><td data-ltr-lahde="sharepoint">{tDok("ltr.lahde_tuotu")}</td></tr>
          )}
        </tbody></table>
        {ta?.huomiot && <p className="tuloste__ehto">{ta.huomiot}</p>}
      </section>

      <section className="tuloste__lohko" data-ltr-osio="kohdat">
        <h3>{tDok("ltr.tarkistuslista")}</h3>
        <table className="tuloste__taulu"><thead><tr><th>{tDok("ltr.kohta")}</th><th>{tDok("ltr.sisalto")}</th><th>{tDok("ltr.tulos")}</th><th>{tDok("ltr.havainto")}</th></tr></thead>
          <tbody>{KOHDAT.map(([n, otsikko]) => {
            const kh = ta?.kohdat?.[n];
            return (
              <tr key={n} data-ltr-kohta={n}>
                <td className="mono">{n}</td><td>{tDok(otsikko)}</td>
                <td data-ltr-tulos={kh?.tulos || (keskenerainen ? "ei_arvioitu" : "")}><b>{keskenerainen && !kh?.tulos ? tDok("ltr.ei_arvioitu") : tulosSana(kh?.tulos)}</b></td>
                <td>{kh?.huomio ?? ""}{n === "7.2" && kh?.vt_peruste ? <div data-ltr-vt-peruste="">{tDok("ltr.vt_peruste", { p: kh.vt_peruste })}</div> : null}</td>
              </tr>
            );
          })}</tbody>
        </table>
        {vtPuuttuu > 0 && <p className="tuloste__ehto">{tDok("ltr.vt_puuttuu_huom", { n: vtPuuttuu })}</p>}
      </section>

      {/* T97 (Toni 30.10.2026 klo 09.25): punchit ja merkinnät alkavat omalta sivultaan; tarkistuslista ja VT-huomio jäävät ensimmäiselle. */}
      <section className="tuloste__lohko" style={SIVUNVAIHTO} data-ltr-osio="punchit" data-ltr-punchit={r.punchit.length}>
        <h3>{tDok("ltr.punchit", { n: r.punchit.length, a: r.punchit.filter((p) => p.avoin).length })}</h3>
        {r.punchit.length === 0 ? <p className="dim">{tDok("ltr.ei_puncheja")}</p> : (
          <table className="tuloste__taulu"><thead><tr><th>#</th><th>{tDok("ltr.kohta")}</th><th>{tDok("ltr.hitsi")}</th><th>{tDok("ltr.vika")}</th>
            <th>{tDok("ltr.luokka")}</th><th>{tDok("ltr.tila")}</th></tr></thead>
            <tbody>{r.punchit.map((p) => (
              <Fragment key={p.id}>
                <tr>
                  <td className="mono">{p.nasta ?? ""}</td><td className="mono">{p.linjatarkastus_kohta ?? ""}</td>
                  <td className="mono">{p.hitsinumero ? naytaHitsinumero(p.hitsinumero) : ""}</td>
                  <td>{p.vika}{p.vastuu ? <span className="dim"> · {p.vastuu}</span> : null}</td><td>{p.luokka ?? "–"}</td>
                  <td>{p.avoin ? tDok("ltr.avoin") : tDok("ltr.korjattu", { pvm: (p.kuittaus_pvm ? naytaPvm(p.kuittaus_pvm) : null) ?? "" })}</td>
                </tr>
                {p.kuvat.length > 0 && (
                  <tr><td colSpan={6}><div className="tuloste__kuvat">{p.kuvat.map((id) => <img key={id} src={api.liiteOsoite(id)} alt="" />)}</div></td></tr>
                )}
              </Fragment>
            ))}</tbody>
          </table>
        )}
      </section>

      <section className="tuloste__lohko" style={SIVUNVAIHTO} data-ltr-osio="merkinnat" data-ltr-pohjat={r.pohjat.length}>
        <h3>{tDok("ltr.merkinnat")}</h3>
        {r.pohjat.length === 0 ? <p className="dim">{tDok("ltr.ei_merkintoja")}</p> : r.pohjat.map((p) => <Pohja key={`${p.laji}${p.id}`} p={p} nastat={r.nastat} />)}
      </section>

      <section className="tuloste__lohko" data-ltr-osio="kuvat" data-ltr-kuvat={kuvat.length}>
        <h3>{tDok("ltr.tarkastuskuvat")}</h3>
        {kuvat.length === 0 ? <p className="dim">{tDok("ltr.ei_kuvia")}</p>
          : <div className="tuloste__kuvat">{kuvat.map((k) => (
              // 274 (T547): kuvateksti on nimi tai laji ja päivä, ei tiedostonimi.
              <figure key={k.id} style={{ margin: 0, width: "46mm", breakInside: "avoid" }} data-ltr-kuva={k.id}>
                <img src={api.liiteOsoite(k.id)} alt={kuvanNimi(k, tDok)} />
                <figcaption className="dim" style={{ fontSize: "8pt" }}>{kuvanNimi(k, tDok)}</figcaption>
              </figure>))}</div>}
      </section>

      {!keskenerainen && <section className="tuloste__lohko" data-ltr-osio="edustaja">
        <h3>{tDok("ltr.edustaja_otsikko")}</h3>
        <p style={{ margin: "4px 0" }}>{tDok("ltr.edustaja", { nimi: ta?.hyvaksynta?.asiakkaan_edustaja ?? ta?.allekirjoitus_nimi ?? "–" })}</p>
        {ta?.allekirjoitus_liite_id
          ? <div data-ltr-allekirjoitus="piirretty"><img className="tuloste__allekirjoitus" src={api.liiteOsoite(ta.allekirjoitus_liite_id)} alt="" />
              <div className="dim">{ta.allekirjoitus_nimi ?? ""}{ta.allekirjoitettu ? ` · ${naytaPvm(ta.allekirjoitettu.slice(0, 10))}` : ""}</div></div>
          : paperit.length > 0
            ? <p data-ltr-allekirjoitus="paperi">{tDok("ltr.paperipoytakirja", { nimet: paperit.map((p) => p.alkuperainen).join(", ") })}</p>
            : <p data-ltr-allekirjoitus="puuttuu">{tDok("ltr.allekirjoitus_puuttuu")}</p>}
        {ta?.hyvaksynta?.a_poikkeama_peruste && (
          <p className="tuloste__ehto">{tDok("ltr.a_poikkeama", { n: ta.hyvaksynta.avoimia_a_hetkella ?? 0, p: ta.hyvaksynta.a_poikkeama_peruste })}</p>
        )}
      </section>}

      <section className="tuloste__lohko" data-ltr-osio="hitsit" data-ltr-hitsit={hitsit.length}>
        <h3>{tDok("ltr.hitsiluettelo", { n: hitsit.length })}</h3>
        <table className="tuloste__taulu"><thead><tr><th>{tDok("ltr.hitsi")}</th><th>{tDok("ltr.lehti")}</th><th>{tDok("ltr.paiva")}</th><th>{tDok("ltr.hitsaaja")}</th><th>VT</th><th>NDT</th></tr></thead>
          <tbody>{hitsit.map((h) => (
            <tr key={h.id} data-ltr-hitsi={h.hitsinumero}>
              <td className="mono">{naytaHitsinumero(h.hitsinumero)}{h.hitsityyppi === "R" ? " R" : ""}</td>
              <td className="mono">{h.piirustuslehti && h.piirustuslehti !== "-" ? h.piirustuslehti : ""}</td>
              <td>{h.ei_toteudu ? tDok("ltr.ei_toteudu") : h.paiva ? naytaPvm(h.paiva) : h.vaihe === "suunniteltu" ? tDok("ltr.suunniteltu") : "–"}</td>
              <td className="mono" data-ltr-hitsaaja={h.hitsaaja ?? ""}>{h.hitsaaja ?? ""}</td>
              <td>{h.vaihe === "hitsattu" ? vtSana(h.vt) : ""}</td>
              <td>{h.ndt.map((x) => `${x.menetelma} ${x.tulos ?? ""}${x.raportti ? ` (${x.raportti})` : ""}`).join(", ")}</td>
            </tr>
          ))}</tbody>
        </table>
      </section>

      <p className="tuloste__alaviite">{tDok("ltr.alaviite", { aika: naytaPvm(r.tulostettu.slice(0, 10)) ?? "" })}</p>
    </TulostePohja>
  );
}
